import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import {
  OrganizationService,
  OrgTerminologyModel,
  AcceptInviteRequest,
  orgInviteEmployeeStorageKey,
} from "src/app/Services/organization.service";
import { OrgContextService } from "src/app/Services/org-context.service";
import { FlowNavigationService } from "src/app/Services/flow-navigation.service";
import { MatDialog } from "@angular/material/dialog";
import {
  ContentActivityService,
  OrgAuditEntry,
} from "src/app/Services/content-activity.service";
import { ContentActivityPanelComponent } from "../content-activity-panel/content-activity-panel.component";
import {
  OrgAccessRoleDefinition,
  OrgRoleFeatureFlags,
  ORG_ROLE_FEATURE_META,
  createDefaultAccessRoles,
  normalizeAccessRoles,
  enabledAccessRoleKeys,
  inviteScopeLabel,
  roleAllowsFeature,
} from "src/app/shared/org-access-roles";

type InviteScope = "Organization" | "Application" | "Team";

interface OrgOption {
  organizationId: string;
  name: string;
}

interface ManageTeamRow {
  teamId: string;
  name: string;
  adminEmail: string;
  editing: boolean;
  draftName: string;
  draftAdminEmail: string;
  contentAccessScope: string;
  draftContentAccessScope: string;
}

interface ManageAppRow {
  applicationId: string;
  name: string;
  adminEmail: string;
  editing: boolean;
  draftName: string;
  draftAdminEmail: string;
  contentAccessScope: string;
  draftContentAccessScope: string;
  teams: ManageTeamRow[];
  addingTeam: boolean;
  newTeamName: string;
  newTeamAdminEmail: string;
}

type OrgAdminTab =
  | "invites"
  | "structure"
  | "roles"
  | "labels"
  | "people"
  | "library"
  | "audit";

type AuditFilter = "all" | "content" | "approvals" | "access" | "org";

interface MemberRoleRow {
  key: string;
  email: string;
  displayName: string;
  profilePicUrl?: string;
  scope: string;
  scopeLabel: string;
  applicationId: string | null;
  teamId: string | null;
  role: string;
  jobRole: string;
  draftRole: string;
  draftJobRole: string;
  saving: boolean;
}

@Component({
  selector: "app-org-admin",
  templateUrl: "./org-admin.component.html",
  styleUrls: ["./org-admin.component.scss"],
})
export class OrgAdminComponent implements OnInit {
  organizationId = "";
  organizationName = "";
  orgOptions: OrgOption[] = [];

  inviteEmail = "";
  inviteScope: InviteScope = "Team";
  /** Scopes the current user's memberships can invite into (mirrors API EnsureCanInviteAsync). */
  inviteScopeOptions: InviteScope[] = ["Team"];
  inviteRole = "Worker";
  inviteJobRole = "";
  applicationId = "";
  teamId = "";
  applications: any[] = [];
  teams: any[] = [];
  managedApps: ManageAppRow[] = [];
  myInvites: any[] = [];
  library: any[] = [];
  feedbackItems: any[] = [];
  joinRequests: any[] = [];
  respondRole = "Worker";
  respondJobRole = "";

  /** Org-level default for non-admin library visibility. */
  orgContentAccessScope = "TeamOnly";
  /** Whether org content may be promoted/published to the public catalog. */
  orgAllowPublicContentPublish = true;
  readonly contentAccessOptions: { value: string; label: string }[] = [
    {
      value: "TeamOnly",
      label: "Teams only — workers see assigned team content",
    },
    {
      value: "ApplicationWide",
      label: "Application-wide — workers see content across their app",
    },
    {
      value: "OrganizationWide",
      label: "Organization-wide — workers see all org content",
    },
  ];
  readonly publicContentPublishOptions: { value: boolean; label: string }[] = [
    {
      value: true,
      label: "Allowed — organization may take published content to public",
    },
    {
      value: false,
      label: "Not allowed — organization content stays internal only",
    },
  ];
  readonly inheritAccessOption = {
    value: "",
    label: "Inherit from parent (org / application default)",
  };

  /** One-by-one + bulk team member invites on People tab */
  bulkRows: { email: string; role: string; jobRole: string }[] = [
    { email: "", role: "Worker", jobRole: "" },
  ];
  bulkDefaultRole = "Worker";
  bulkDefaultJobRole = "";
  bulkScope: "Team" | "Application" | "Organization" = "Team";
  bulkApplicationId = "";
  bulkTeamId = "";
  bulkTeams: any[] = [];
  bulkBusy = false;

  discontinueEmail = "";
  ownedItems: any[] = [];
  reassignmentEmail = "";
  busy = false;
  loading = false;

  /** Invites | Structure | Labels | People | Library */
  activeTab: OrgAdminTab = "invites";
  @ViewChild("tabStrip") tabStrip?: ElementRef<HTMLElement>;
  orgPendingInvites: any[] = [];
  canApproveContent = false;
  private defaultTabPicked = false;

  /** Contextual invite panel (org/app/team prefilled). */
  contextualInviteOpen = false;
  contextualScope: "Organization" | "Application" | "Team" = "Organization";
  contextualApplicationId = "";
  contextualTeamId = "";
  contextualLabel = "";
  contextualEmail = "";
  contextualRole = "Worker";
  contextualJobRole = "";

  isOrgAdmin = false;
  isAppAdmin = false;
  isTeamAdmin = false;
  canManageMemberRoles = false;
  canManageOrganization = false;
  memberships: any[] = [];

  /** People tab — current members with their role per scope. */
  memberRows: MemberRoleRow[] = [];
  membersLoading = false;
  membersError: string | null = null;
  membersSearch = "";
  membersPageIndex = 0;
  readonly membersPageSize = 25;
  membersTotal = 0;
  unassignedMemberCount = 0;
  private membersLoadedFor: string | null = null;
  private membersSearchTimer: ReturnType<typeof setTimeout> | null = null;

  terminology: OrgTerminologyModel = {
    roleLabel: "Role",
    statusLabel: "Status",
    customRulesLabel: "Rules",
    departmentLabel: "Department",
    locationLabel: "Location",
    divisionLabel: "Division",
    shiftLabel: "Shift",
    applicationLabel: "Application",
    teamLabel: "Team",
    roles: [],
    statuses: [],
    customRules: [],
    departments: [],
    locations: [],
    divisions: [],
    shifts: [],
    accessRoles: createDefaultAccessRoles(),
  };
  accessRoles: OrgAccessRoleDefinition[] = createDefaultAccessRoles();
  readonly featureMeta = ORG_ROLE_FEATURE_META;
  expandedRoleKey: string | null = "OrgAdmin";
  accessRolesBusy = false;
  /** Cached for templates — avoid allocating new arrays every change-detection cycle. */
  visibleTabs: { id: OrgAdminTab; label: string }[] = [
    { id: "invites", label: "Invites" },
  ];
  inviteRoleOptions: string[] = [];
  joinRoleOptions: string[] = [];
  contextualRoleOptions: string[] = [];
  jobRoleOptions: string[] = [];
  roleFeatureSummaries: Record<string, string[]> = {};
  /** Stable empty list for templates — never allocate `|| []` in *ngFor. */
  private readonly emptyFeatureLabels: string[] = [];
  pendingJoinCount = 0;
  terminologyDrafts: Record<string, string> = {
    roles: "",
    statuses: "",
    customRules: "",
    departments: "",
    locations: "",
    divisions: "",
    shifts: "",
  };
  terminologyBusy = false;

  readonly terminologyValueGroups: {
    valuesKey: keyof OrgTerminologyModel;
    labelKey: keyof OrgTerminologyModel;
    title: string;
    placeholder: string;
  }[] = [
    {
      valuesKey: "roles",
      labelKey: "roleLabel",
      title: "Job titles",
      placeholder: "e.g. Operator, Supervisor",
    },
    {
      valuesKey: "statuses",
      labelKey: "statusLabel",
      title: "Status flows",
      placeholder: "e.g. Draft, In Review, Approved",
    },
    {
      valuesKey: "customRules",
      labelKey: "customRulesLabel",
      title: "Custom rules",
      placeholder: "e.g. PPE required",
    },
    {
      valuesKey: "departments",
      labelKey: "departmentLabel",
      title: "Departments",
      placeholder: "e.g. Production",
    },
    {
      valuesKey: "locations",
      labelKey: "locationLabel",
      title: "Locations",
      placeholder: "e.g. Plant A",
    },
    {
      valuesKey: "divisions",
      labelKey: "divisionLabel",
      title: "Divisions",
      placeholder: "e.g. North",
    },
    {
      valuesKey: "shifts",
      labelKey: "shiftLabel",
      title: "Shifts",
      placeholder: "e.g. Day, Night",
    },
  ];

  /** Audit log tab */
  auditEntries: OrgAuditEntry[] = [];
  filteredAuditEntries: OrgAuditEntry[] = [];
  auditLoading = false;
  auditError: string | null = null;
  auditFilter: AuditFilter = "all";
  auditSearch = "";
  private auditLoadedFor: string | null = null;
  readonly auditFilters: { id: AuditFilter; label: string; icon: string }[] = [
    { id: "all", label: "Everything", icon: "history" },
    { id: "content", label: "Guides & packs", icon: "menu_book" },
    { id: "approvals", label: "Approvals", icon: "fact_check" },
    { id: "access", label: "Access changes", icon: "lock" },
    { id: "org", label: "People & structure", icon: "groups" },
  ];

  constructor(
    private organizationService: OrganizationService,
    private orgContext: OrgContextService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private flowNavigation: FlowNavigationService,
    private contentActivity: ContentActivityService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.rebuildDerivedLists();
    const preferred =
      localStorage.getItem("activeOrganizationId") ||
      this.orgContext.snapshot.activeOrganization?.organizationId ||
      undefined;

    this.orgContext.load(preferred).subscribe({
      next: () => {
        this.syncOrgFromContext();
        this.loading = false;
        this.refresh();
        this.handleInviteDeepLink();
      },
      error: () => {
        this.loading = false;
        this.rebuildDerivedLists();
        this.refresh();
        this.handleInviteDeepLink();
      },
    });
  }

  get hasOrganization(): boolean {
    return !!this.organizationId;
  }

  get canManageStructure(): boolean {
    return this.isOrgAdmin || this.isAppAdmin || this.isTeamAdmin;
  }

  get canManagePeople(): boolean {
    return this.isOrgAdmin || this.isAppAdmin || this.canManageMemberRoles;
  }

  setTab(tab: OrgAdminTab): void {
    this.activeTab = tab;
    if (tab === "audit") this.loadAuditLog();
    if (tab === "people") this.loadMembers();
    setTimeout(() => {
      const active = this.tabStrip?.nativeElement.querySelector(".org-tab.active") as HTMLElement | null;
      active?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    });
  }

  get activeTabIndex(): number {
    return this.visibleTabs.findIndex((t) => t.id === this.activeTab);
  }

  prevTab(): void {
    const i = this.activeTabIndex;
    if (i > 0) this.setTab(this.visibleTabs[i - 1].id);
  }

  nextTab(): void {
    const i = this.activeTabIndex;
    if (i >= 0 && i < this.visibleTabs.length - 1) this.setTab(this.visibleTabs[i + 1].id);
  }

  loadMembers(force = false): void {
    if (!this.organizationId || !this.canManagePeople) return;
    const key = `${this.organizationId}|${this.membersSearch.trim()}|${this.membersPageIndex}`;
    if (!force && this.membersLoadedFor === key) return;
    this.membersLoading = true;
    this.membersError = null;
    this.organizationService
      .listMembers(this.organizationId, {
        q: this.membersSearch.trim() || undefined,
        pageIndex: this.membersPageIndex,
        pageSize: this.membersPageSize,
      })
      .subscribe({
        next: (res) => {
          this.membersLoadedFor = key;
          this.membersLoading = false;
          this.membersTotal = res?.totalCount ?? 0;
          this.memberRows = this.toMemberRows(res?.members || []);
          this.unassignedMemberCount = this.memberRows.filter((r) => !r.role).length;
        },
        error: (err) => {
          this.membersLoading = false;
          this.memberRows = [];
          this.membersTotal = 0;
          this.unassignedMemberCount = 0;
          this.membersError =
            err?.status === 403 || err?.status === 401
              ? "Your role does not allow viewing members."
              : err?.error?.error || "Could not load members.";
        },
      });
  }

  onMembersSearchChange(): void {
    if (this.membersSearchTimer) clearTimeout(this.membersSearchTimer);
    this.membersSearchTimer = setTimeout(() => {
      this.membersPageIndex = 0;
      this.loadMembers();
    }, 300);
  }

  get membersPageCount(): number {
    return Math.max(1, Math.ceil(this.membersTotal / this.membersPageSize));
  }

  changeMembersPage(delta: number): void {
    const next = this.membersPageIndex + delta;
    if (next < 0 || next >= this.membersPageCount) return;
    this.membersPageIndex = next;
    this.loadMembers();
  }

  private toMemberRows(members: any[]): MemberRoleRow[] {
    const rows: MemberRoleRow[] = [];
    for (const m of members) {
      const email = (m.email || m.Email || "").toLowerCase();
      if (!email) continue;
      const displayName = m.displayName || m.DisplayName || m.userName || m.UserName || "";
      const scopes: any[] = m.memberships || m.Memberships || [];
      const list = scopes.length ? scopes : [m];
      for (const s of list) {
        const appId = s.applicationId || s.ApplicationId || null;
        const teamId = s.teamId || s.TeamId || null;
        const role = s.role || s.Role || "";
        const jobRole = s.jobRole || s.JobRole || "";
        rows.push({
          key: `${email}|${appId || ""}|${teamId || ""}`,
          email,
          displayName,
          profilePicUrl: m.profilePicUrl || m.ProfilePicUrl,
          scope: s.scope || s.Scope || (teamId ? "Team" : appId ? "Application" : "Organization"),
          scopeLabel: this.memberScopeLabel(s, appId, teamId),
          applicationId: appId,
          teamId,
          role,
          jobRole,
          draftRole: role,
          draftJobRole: jobRole,
          saving: false,
        });
      }
    }
    return rows.sort((a, b) => (a.role ? 1 : 0) - (b.role ? 1 : 0));
  }

  private memberScopeLabel(s: any, appId: string | null, teamId: string | null): string {
    const appName = s.applicationName || s.ApplicationName || appId;
    const teamName = s.teamName || s.TeamName || teamId;
    if (teamId) return `${this.terminology.teamLabel || "Team"}: ${teamName}${appName ? ` (${appName})` : ""}`;
    if (appId) return `${this.terminology.applicationLabel || "Application"}: ${appName}`;
    return "Whole organization";
  }

  isSelf(row: MemberRoleRow): boolean {
    return this.orgContext.currentUserEmail() === row.email;
  }

  memberRoleDirty(row: MemberRoleRow): boolean {
    return (
      !!row.draftRole &&
      (row.draftRole !== row.role || (row.draftJobRole || "") !== (row.jobRole || ""))
    );
  }

  saveMemberRole(row: MemberRoleRow): void {
    if (!this.organizationId || !row.draftRole || row.saving) return;
    row.saving = true;
    this.organizationService
      .updateMemberRole(this.organizationId, {
        email: row.email,
        role: row.draftRole,
        jobRole: row.draftJobRole || null,
        applicationId: row.applicationId,
        teamId: row.teamId,
      })
      .subscribe({
        next: () => {
          row.saving = false;
          const wasUnassigned = !row.role;
          row.role = row.draftRole;
          row.jobRole = row.draftJobRole;
          if (wasUnassigned) this.unassignedMemberCount = Math.max(0, this.unassignedMemberCount - 1);
          this.toastr.success(
            `${row.displayName || row.email} is now ${this.roleDisplayName(row.role)}`
          );
        },
        error: (err) => {
          row.saving = false;
          this.toastr.error(err?.error?.error || "Could not update role");
        },
      });
  }

  resetMemberRole(row: MemberRoleRow): void {
    row.draftRole = row.role;
    row.draftJobRole = row.jobRole;
  }

  trackByMemberRow(_: number, row: MemberRoleRow): string {
    return row.key;
  }

  loadAuditLog(force = false): void {
    if (!this.organizationId) return;
    if (!force && this.auditLoadedFor === this.organizationId) return;
    this.auditLoading = true;
    this.auditError = null;
    this.contentActivity.orgAuditLog(this.organizationId, undefined, 300).subscribe({
      next: (rows) => {
        this.auditEntries = rows || [];
        this.auditLoadedFor = this.organizationId;
        this.auditLoading = false;
        this.applyAuditFilter();
      },
      error: (err) => {
        this.auditLoading = false;
        this.auditEntries = [];
        this.filteredAuditEntries = [];
        this.auditError =
          err?.status === 403 || err?.status === 401
            ? "Only organization, application or team admins can view the audit log."
            : err?.error?.message || "Could not load the audit log.";
      },
    });
  }

  setAuditFilter(filter: AuditFilter): void {
    this.auditFilter = filter;
    this.applyAuditFilter();
  }

  auditCount(filter: AuditFilter): number {
    return this.auditEntries.filter((e) => this.matchesAuditFilter(e, filter)).length;
  }

  applyAuditFilter(): void {
    const q = this.auditSearch.trim().toLowerCase();
    this.filteredAuditEntries = this.auditEntries.filter((e) => {
      if (!this.matchesAuditFilter(e, this.auditFilter)) return false;
      if (!q) return true;
      return [e.actorName, e.actorEmail, e.action, e.targetTitle, e.targetId, e.comment]
        .some((v) => String(v || "").toLowerCase().includes(q));
    });
  }

  private matchesAuditFilter(e: OrgAuditEntry, filter: AuditFilter): boolean {
    const isContent = e.targetType === "Guide" || e.targetType === "GuidePack";
    switch (filter) {
      case "content":
        return isContent;
      case "approvals":
        return isContent && e.category === "Approval";
      case "access":
        return isContent && e.category === "Access";
      case "org":
        return !isContent;
      default:
        return true;
    }
  }

  openAuditTarget(entry: OrgAuditEntry): void {
    const id = entry.targetId || entry.contentId;
    if (!id) return;
    ContentActivityPanelComponent.open(this.dialog, {
      contentType: entry.targetType === "GuidePack" ? "guidePack" : "guide",
      contentId: id,
      title: entry.targetTitle,
    });
  }

  trackByTabId(_: number, tab: { id: OrgAdminTab }): string {
    return tab.id;
  }

  trackByRoleKey(_: number, role: OrgAccessRoleDefinition): string {
    return role.key;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private pickDefaultTab(): void {
    if (this.defaultTabPicked) return;
    this.defaultTabPicked = true;
    const requested = this.route.snapshot.queryParamMap.get("tab");
    if (requested && this.visibleTabs.some((t) => t.id === requested)) {
      this.setTab(requested as OrgAdminTab);
      return;
    }
    if (this.myInvites?.length) {
      this.activeTab = "invites";
      return;
    }
    const visible = this.visibleTabs.map((t) => t.id);
    if (visible.includes("structure")) {
      this.activeTab = "structure";
      return;
    }
    this.activeTab = visible[0] || "invites";
  }

  isEmailPending(email?: string | null): boolean {
    if (!email) return false;
    const normalized = email.trim().toLowerCase();
    return (this.orgPendingInvites || []).some((inv) => {
      const status = (inv.status || inv.Status || "").toLowerCase();
      if (status && status !== "pending") return false;
      const invEmail = (inv.email || inv.Email || "").trim().toLowerCase();
      return invEmail === normalized;
    });
  }

  roleDisplayName(key: string): string {
    const match = this.accessRoles.find((r) => r.key === key);
    return match?.displayName || key;
  }

  readonly inviteScopeLabel = inviteScopeLabel;

  toggleRoleExpanded(key: string): void {
    this.expandedRoleKey = this.expandedRoleKey === key ? null : key;
  }

  onRoleEnabledChange(role: OrgAccessRoleDefinition, enabled: boolean): void {
    if (role.required) {
      role.enabled = true;
      return;
    }
    role.enabled = enabled;
    this.rebuildRoleOptionLists();
  }

  onFeatureToggle(
    role: OrgAccessRoleDefinition,
    feature: keyof OrgRoleFeatureFlags,
    value: boolean
  ): void {
    role.features = { ...role.features, [feature]: value };
    this.rebuildFeatureSummaries();
  }

  /** Rebuild tab + role option caches after org/role data changes. */
  private rebuildDerivedLists(): void {
    this.rebuildVisibleTabs();
    this.rebuildRoleOptionLists();
    this.rebuildFeatureSummaries();
    this.jobRoleOptions = this.terminology?.roles || [];
    this.pendingJoinCount = this.joinRequests?.length || 0;
    this.rebuildInviteScopeOptions();
  }

  private rebuildVisibleTabs(): void {
    const tabs: { id: OrgAdminTab; label: string }[] = [
      { id: "invites", label: "Invites" },
    ];
    if (this.hasOrganization && this.canManageStructure) {
      tabs.push({ id: "structure", label: "Structure" });
    }
    if (this.hasOrganization && this.isOrgAdmin) {
      tabs.push({ id: "roles", label: "Roles" });
      tabs.push({ id: "labels", label: "Labels" });
    }
    if (this.hasOrganization && this.canManagePeople) {
      tabs.push({ id: "people", label: "People" });
    }
    if (this.hasOrganization) {
      tabs.push({ id: "library", label: "Library" });
    }
    if (this.hasOrganization && this.canManageStructure) {
      tabs.push({ id: "audit", label: "Audit log" });
    }
    this.visibleTabs = tabs;
  }

  private rebuildRoleOptionLists(): void {
    const enabled = enabledAccessRoleKeys(this.accessRoles);
    this.inviteRoleOptions = enabled;
    this.joinRoleOptions = [
      "TeamAdmin",
      "Worker",
      "Author",
      "Reviewer",
      "Approver",
    ].filter((r) => enabled.includes(r));
    this.rebuildContextualRoleOptions();
  }

  private rebuildContextualRoleOptions(): void {
    let candidates: string[];
    if (this.contextualScope === "Organization") {
      candidates = ["OrgAdmin", "Worker", "Author", "Reviewer", "Approver"];
    } else if (this.contextualScope === "Application") {
      candidates = ["AppAdmin", "Worker", "Author", "Reviewer", "Approver"];
    } else {
      candidates = ["TeamAdmin", "Worker", "Author", "Reviewer", "Approver"];
    }
    const enabled = this.inviteRoleOptions;
    this.contextualRoleOptions = candidates.filter((r) =>
      enabled.includes(r)
    );
    if (!this.contextualRoleOptions.includes(this.contextualRole)) {
      this.contextualRole = this.contextualRoleOptions.includes("Worker")
        ? "Worker"
        : this.contextualRoleOptions[0] || "";
    }
  }

  private inviteErrorMessage(err: any): string {
    const body = err?.error;
    if (typeof body === "string" && body.trim()) return body;
    return (
      body?.error ||
      body?.message ||
      body?.title ||
      (err?.status === 0 ? "Could not reach the server" : "") ||
      "Invite failed"
    );
  }

  private isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  private rebuildFeatureSummaries(): void {
    const summaries: Record<string, string[]> = {};
    for (const role of this.accessRoles || []) {
      summaries[role.key] = this.featureMeta
        .filter((f) => role.features?.[f.key])
        .map((f) => f.label);
    }
    this.roleFeatureSummaries = summaries;
  }

  /** Stable reference for *ngFor — avoids `|| []` allocating every CD cycle. */
  featureLabelsFor(roleKey: string): string[] {
    return this.roleFeatureSummaries[roleKey] || this.emptyFeatureLabels;
  }

  saveAccessRoles(): void {
    if (!this.organizationId || !this.isOrgAdmin) return;
    this.accessRolesBusy = true;
    const accessRoles = normalizeAccessRoles(this.accessRoles);
    this.organizationService
      .updateTerminology(this.organizationId, {
        ...this.terminology,
        roles: this.terminology.roles || [],
        statuses: this.terminology.statuses || [],
        customRules: this.terminology.customRules || [],
        departments: this.terminology.departments || [],
        locations: this.terminology.locations || [],
        divisions: this.terminology.divisions || [],
        shifts: this.terminology.shifts || [],
        accessRoles,
      })
      .subscribe({
        next: (term) => {
          this.accessRolesBusy = false;
          if (term) {
            this.applyTerminology(term);
          }
          this.toastr.success("Access roles saved");
          this.orgContext.load(this.organizationId).subscribe();
        },
        error: (err) => {
          this.accessRolesBusy = false;
          this.toastr.error(err?.error?.error || "Could not save roles");
        },
      });
  }

  private applyTerminology(term: any): void {
    if (!term) return;
    this.terminology = {
      ...this.terminology,
      ...term,
      roles: term.roles || [],
      statuses: term.statuses || [],
      customRules: term.customRules || [],
      departments: term.departments || [],
      locations: term.locations || [],
      divisions: term.divisions || [],
      shifts: term.shifts || [],
      accessRoles: normalizeAccessRoles(term.accessRoles),
    };
    this.accessRoles = normalizeAccessRoles(term.accessRoles);
    this.rebuildDerivedLists();
  }

  onOrganizationChange(organizationId: string): void {
    if (!organizationId || organizationId === this.organizationId) return;
    this.organizationId = organizationId;
    const match = this.orgOptions.find((o) => o.organizationId === organizationId);
    this.organizationName = match?.name || organizationId;
    this.applicationId = "";
    this.teamId = "";
    this.teams = [];
    this.managedApps = [];
    this.defaultTabPicked = false;
    this.orgContext.enterOrganizationMode(organizationId).subscribe({
      next: () => {
        this.syncOrgFromContext();
        this.refresh();
      },
    });
  }

  refresh(): void {
    this.organizationService.myInvites().subscribe({
      next: (invites) => {
        this.myInvites = invites || [];
        this.pickDefaultTab();
      },
      error: () => {
        this.myInvites = [];
        this.pickDefaultTab();
      },
    });
    if (!this.organizationId) return;
    this.organizationService.getOrganization(this.organizationId).subscribe({
      next: (org) => {
        this.orgContentAccessScope =
          org?.contentAccessScope || org?.ContentAccessScope || "TeamOnly";
        this.orgAllowPublicContentPublish =
          this.resolveAllowPublicContentPublish(org);
        if (org?.name) this.organizationName = org.name;
      },
      error: () => {
        this.orgContentAccessScope = "TeamOnly";
        this.orgAllowPublicContentPublish = true;
      },
    });
    this.organizationService.listApplications(this.organizationId).subscribe({
      next: (apps) => {
        this.applications = apps || [];
        this.loadManagedApps(apps || []);
      },
    });
    this.organizationService.listLibrary(this.organizationId).subscribe({
      next: (items) => (this.library = items || []),
    });
    this.organizationService.listFeedback(this.organizationId).subscribe({
      next: (items) => (this.feedbackItems = items || []),
      error: () => (this.feedbackItems = []),
    });
    this.loadTerminology();
    this.loadOrgPendingInvites();
    this.loadJoinRequests();
    if (this.activeTab === "people") this.loadMembers(true);
  }

  private loadOrgPendingInvites(): void {
    if (!this.organizationId || !(this.isOrgAdmin || this.isAppAdmin || this.isTeamAdmin)) {
      this.orgPendingInvites = [];
      return;
    }
    this.organizationService.listOrgInvites(this.organizationId).subscribe({
      next: (invites) => {
        this.orgPendingInvites = (invites || []).filter((inv) => {
          const status = (inv.status || inv.Status || "Pending").toLowerCase();
          return status === "pending";
        });
      },
      error: () => (this.orgPendingInvites = []),
    });
  }

  private loadJoinRequests(): void {
    if (!this.organizationId || !(this.isOrgAdmin || this.isAppAdmin || this.isTeamAdmin)) {
      this.joinRequests = [];
      this.pendingJoinCount = 0;
      return;
    }
    this.organizationService.listJoinRequests(this.organizationId).subscribe({
      next: (items) => {
        this.joinRequests = (items || []).filter((r) => {
          const status = (r.status || r.Status || "").toLowerCase();
          return status === "pending";
        });
        this.pendingJoinCount = this.joinRequests.length;
      },
      error: () => {
        this.joinRequests = [];
        this.pendingJoinCount = 0;
      },
    });
  }

  loadTerminology(): void {
    if (!this.organizationId) return;
    this.organizationService.getTerminology(this.organizationId).subscribe({
      next: (term) => {
        if (!term) {
          this.accessRoles = createDefaultAccessRoles();
          this.rebuildDerivedLists();
          return;
        }
        this.applyTerminology(term);
      },
      error: () => {
        this.accessRoles = createDefaultAccessRoles();
        this.rebuildDerivedLists();
      },
    });
  }

  getTerminologyValues(key: keyof OrgTerminologyModel): string[] {
    const list = this.terminology[key];
    return Array.isArray(list) ? (list as string[]) : [];
  }

  addTerminologyValue(valuesKey: keyof OrgTerminologyModel): void {
    const draftKey = String(valuesKey);
    const raw = (this.terminologyDrafts[draftKey] || "").trim();
    if (!raw) return;
    const current = this.getTerminologyValues(valuesKey);
    if (current.some((v) => v.toLowerCase() === raw.toLowerCase())) {
      this.toastr.info(`"${raw}" is already added`);
      this.terminologyDrafts[draftKey] = "";
      return;
    }
    (this.terminology as any)[valuesKey] = [...current, raw];
    this.terminologyDrafts[draftKey] = "";
    if (valuesKey === "roles") {
      this.jobRoleOptions = this.terminology.roles || [];
    }
  }

  removeTerminologyValue(
    valuesKey: keyof OrgTerminologyModel,
    index: number
  ): void {
    const current = this.getTerminologyValues(valuesKey);
    (this.terminology as any)[valuesKey] = current.filter((_, i) => i !== index);
    if (valuesKey === "roles") {
      this.jobRoleOptions = this.terminology.roles || [];
    }
  }

  onTerminologyValueKeydown(
    event: KeyboardEvent,
    valuesKey: keyof OrgTerminologyModel
  ): void {
    if (event.key === "Enter") {
      event.preventDefault();
      this.addTerminologyValue(valuesKey);
    }
  }

  saveTerminology(): void {
    if (!this.organizationId || !this.isOrgAdmin) return;
    this.terminologyBusy = true;
    this.organizationService
      .updateTerminology(this.organizationId, {
        ...this.terminology,
        roles: this.terminology.roles || [],
        statuses: this.terminology.statuses || [],
        customRules: this.terminology.customRules || [],
        departments: this.terminology.departments || [],
        locations: this.terminology.locations || [],
        divisions: this.terminology.divisions || [],
        shifts: this.terminology.shifts || [],
        // Preserve access roles when saving labels-only.
        accessRoles: normalizeAccessRoles(this.accessRoles),
      })
      .subscribe({
        next: (term) => {
          this.terminologyBusy = false;
          if (term) {
            this.applyTerminology(term);
          }
          this.toastr.success("Terminology saved");
          this.orgContext.load(this.organizationId).subscribe();
        },
        error: (err) => {
          this.terminologyBusy = false;
          this.toastr.error(err?.error?.error || "Could not save terminology");
        },
      });
  }

  onApplicationChange(): void {
    this.teamId = "";
    this.teams = [];
    if (!this.organizationId || !this.applicationId) return;
    this.organizationService
      .listTeams(this.organizationId, this.applicationId)
      .subscribe({ next: (teams) => (this.teams = teams || []) });
  }

  sendInvite(): void {
    if (!this.organizationId || !this.inviteEmail.trim()) {
      this.toastr.error("Organization and email are required");
      return;
    }
    this.busy = true;
    this.organizationService
      .invite({
        organizationId: this.organizationId,
        email: this.inviteEmail.trim(),
        scope: this.inviteScope,
        role: this.inviteRole,
        jobRole: this.inviteJobRole || undefined,
        applicationId:
          this.inviteScope !== "Organization" ? this.applicationId : undefined,
        teamId: this.inviteScope === "Team" ? this.teamId : undefined,
      })
      .subscribe({
        next: () => {
          this.busy = false;
          this.toastr.success("Invite sent — pending acceptance");
          this.inviteEmail = "";
          this.inviteJobRole = "";
          this.loadOrgPendingInvites();
        },
        error: (err) => {
          this.busy = false;
          this.toastr.error(this.inviteErrorMessage(err));
        },
      });
  }

  openInvitePeople(): void {
    if (this.inviteScopeOptions.includes("Organization")) {
      this.openOrgInvite();
      return;
    }
    if (this.openOwnScopeInvite()) return;
    if (this.canManagePeople) {
      this.setTab("people");
      return;
    }
    this.toastr.error("Your role does not allow inviting people to this organization");
  }

  openOrgInvite(): void {
    if (!this.inviteScopeOptions.includes("Organization")) {
      this.toastr.error("Your role does not allow organization-wide invites");
      return;
    }
    this.contextualScope = "Organization";
    this.contextualApplicationId = "";
    this.contextualTeamId = "";
    this.contextualLabel = this.organizationName || "Organization";
    this.contextualEmail = "";
    this.contextualRole = "Worker";
    this.contextualJobRole = "";
    this.rebuildContextualRoleOptions();
    this.contextualInviteOpen = true;
  }

  /** Opens the invite dialog for the app/team the user can invite into (app/team admins). */
  private openOwnScopeInvite(): boolean {
    const inviter = this.memberships
      .filter((m) =>
        roleAllowsFeature(this.accessRoles, String(m?.role || m?.Role || ""), "inviteMembers")
      )
      .map((m) => {
        const id = (v: any) => {
          const s = String(v ?? "").trim();
          return /^(none|null|0{8}-0{4}-0{4}-0{4}-0{12})$/i.test(s) ? "" : s;
        };
        return {
          applicationId: id(m.applicationId || m.ApplicationId),
          teamId: id(m.teamId || m.TeamId),
        };
      })
      .filter((m) => m.applicationId || m.teamId)
      .sort((a, b) => Number(!!a.teamId) - Number(!!b.teamId))[0];
    if (!inviter) return false;

    const app =
      this.managedApps.find((a) => a.applicationId === inviter.applicationId) ||
      this.managedApps.find((a) =>
        (a.teams || []).some((t) => t.teamId === inviter.teamId)
      );
    const appRow: ManageAppRow =
      app ||
      ({
        applicationId: inviter.applicationId,
        name:
          this.applications.find(
            (a: any) => (a.applicationId || a.ApplicationId) === inviter.applicationId
          )?.name || "Application",
        teams: [],
      } as any);

    if (!inviter.teamId) {
      this.openAppInvite(appRow);
      return true;
    }
    const team: ManageTeamRow =
      (appRow.teams || []).find((t) => t.teamId === inviter.teamId) ||
      ({ teamId: inviter.teamId, name: "Team" } as any);
    this.openTeamInvite(appRow, team);
    return true;
  }

  openAppInvite(app: ManageAppRow): void {
    this.contextualScope = "Application";
    this.contextualApplicationId = app.applicationId;
    this.contextualTeamId = "";
    this.contextualLabel = app.name;
    this.contextualEmail = "";
    this.contextualRole = "Worker";
    this.contextualJobRole = "";
    this.rebuildContextualRoleOptions();
    this.contextualInviteOpen = true;
  }

  openTeamInvite(app: ManageAppRow, team: ManageTeamRow): void {
    this.contextualScope = "Team";
    this.contextualApplicationId = app.applicationId;
    this.contextualTeamId = team.teamId;
    this.contextualLabel = `${app.name} / ${team.name}`;
    this.contextualEmail = "";
    this.contextualRole = "Worker";
    this.contextualJobRole = "";
    this.rebuildContextualRoleOptions();
    this.contextualInviteOpen = true;
  }

  closeContextualInvite(): void {
    this.contextualInviteOpen = false;
  }

  sendContextualInvite(): void {
    const email = this.contextualEmail.trim();
    if (!this.organizationId || !email) {
      this.toastr.error("Email is required");
      return;
    }
    if (!this.isValidEmail(email)) {
      this.toastr.error("Enter a valid email address");
      return;
    }
    if (!this.contextualRole) {
      this.toastr.error("Select a role");
      return;
    }
    this.busy = true;
    this.organizationService
      .invite({
        organizationId: this.organizationId,
        email,
        scope: this.contextualScope,
        role: this.contextualRole,
        jobRole: this.contextualJobRole || undefined,
        applicationId:
          this.contextualScope !== "Organization"
            ? this.contextualApplicationId
            : undefined,
        teamId:
          this.contextualScope === "Team" ? this.contextualTeamId : undefined,
      })
      .subscribe({
        next: () => {
          this.busy = false;
          this.toastr.success(`Invite sent to ${email} — pending acceptance`);
          this.contextualInviteOpen = false;
          this.loadOrgPendingInvites();
        },
        error: (err) => {
          this.busy = false;
          this.toastr.error(this.inviteErrorMessage(err));
        },
      });
  }

  respondJoin(req: any, accept: boolean): void {
    if (!this.organizationId) return;
    const requestId = req?.requestId || req?.RequestId;
    if (!requestId) return;
    this.busy = true;
    this.organizationService
      .respondJoinRequest(this.organizationId, requestId, {
        accept,
        role: accept ? this.respondRole || "Worker" : undefined,
        jobRole: accept ? this.respondJobRole || undefined : undefined,
      })
      .subscribe({
        next: () => {
          this.busy = false;
          this.toastr.success(accept ? "Join request accepted" : "Join request rejected");
          this.loadJoinRequests();
          this.orgContext.load(this.organizationId).subscribe();
        },
        error: (err) => {
          this.busy = false;
          this.toastr.error(err?.error?.error || "Could not respond");
        },
      });
  }

  addBulkRow(): void {
    this.bulkRows = [
      ...this.bulkRows,
      {
        email: "",
        role: this.bulkDefaultRole || "Worker",
        jobRole: this.bulkDefaultJobRole || "",
      },
    ];
  }

  removeBulkRow(index: number): void {
    this.bulkRows = this.bulkRows.filter((_, i) => i !== index);
    if (!this.bulkRows.length) this.addBulkRow();
  }

  onBulkApplicationChange(): void {
    this.bulkTeamId = "";
    this.bulkTeams = [];
    if (!this.organizationId || !this.bulkApplicationId) return;
    this.organizationService
      .listTeams(this.organizationId, this.bulkApplicationId)
      .subscribe({ next: (teams) => (this.bulkTeams = teams || []) });
  }

  onBulkCsvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      const parsed = this.parseInviteCsv(text);
      if (!parsed.length) {
        this.toastr.warning("No valid rows found (need email column)");
        return;
      }
      this.bulkRows = parsed;
      this.toastr.success(`Loaded ${parsed.length} row(s) from file`);
    };
    reader.readAsText(file);
    input.value = "";
  }

  private parseInviteCsv(text: string): { email: string; role: string; jobRole: string }[] {
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) return [];
    const split = (line: string) =>
      line.split(/[,;\t]/).map((c) => c.trim().replace(/^"|"$/g, ""));
    const header = split(lines[0]).map((h) => h.toLowerCase());
    const hasHeader =
      header.includes("email") || header[0]?.includes("@") === false;
    const emailIdx = hasHeader ? Math.max(0, header.indexOf("email")) : 0;
    const roleIdx = hasHeader ? header.indexOf("role") : 1;
    const jobIdx = hasHeader
      ? Math.max(header.indexOf("jobrole"), header.indexOf("job_role"))
      : 2;
    const start = hasHeader && header.includes("email") ? 1 : 0;
    const rows: { email: string; role: string; jobRole: string }[] = [];
    for (let i = start; i < lines.length; i++) {
      const cols = split(lines[i]);
      const email = (cols[emailIdx] || "").trim();
      if (!email || !email.includes("@")) continue;
      rows.push({
        email,
        role: (roleIdx >= 0 ? cols[roleIdx] : "") || this.bulkDefaultRole || "Worker",
        jobRole: (jobIdx >= 0 ? cols[jobIdx] : "") || this.bulkDefaultJobRole || "",
      });
    }
    return rows;
  }

  sendBulkInvites(): void {
    if (!this.organizationId) {
      this.toastr.error("Select an organization first");
      return;
    }
    const invites = this.bulkRows
      .map((r) => ({
        email: (r.email || "").trim(),
        role: r.role || this.bulkDefaultRole || "Worker",
        jobRole: r.jobRole || this.bulkDefaultJobRole || undefined,
      }))
      .filter((r) => r.email.includes("@"));
    if (!invites.length) {
      this.toastr.error("Add at least one valid email");
      return;
    }
    if (this.bulkScope === "Team" && !this.bulkTeamId) {
      this.toastr.error("Select a team for team invites");
      return;
    }
    if (this.bulkScope !== "Organization" && !this.bulkApplicationId) {
      this.toastr.error("Select an application");
      return;
    }
    this.bulkBusy = true;
    this.organizationService
      .bulkInvite({
        organizationId: this.organizationId,
        scope: this.bulkScope,
        applicationId:
          this.bulkScope !== "Organization" ? this.bulkApplicationId : undefined,
        teamId: this.bulkScope === "Team" ? this.bulkTeamId : undefined,
        defaultRole: this.bulkDefaultRole || "Worker",
        defaultJobRole: this.bulkDefaultJobRole || undefined,
        invites,
      })
      .subscribe({
        next: (res) => {
          this.bulkBusy = false;
          const created = res?.created?.length || 0;
          const errors = res?.errors?.length || 0;
          this.toastr.success(
            `Sent ${created} invite(s)` + (errors ? `; ${errors} skipped` : "")
          );
          this.bulkRows = [{ email: "", role: "Worker", jobRole: "" }];
          this.loadOrgPendingInvites();
        },
        error: (err) => {
          this.bulkBusy = false;
          this.toastr.error(err?.error?.error || "Bulk invite failed");
        },
      });
  }

  startEditApp(app: ManageAppRow): void {
    app.editing = true;
    app.draftName = app.name;
    app.draftAdminEmail = app.adminEmail;
    app.draftContentAccessScope = app.contentAccessScope || "";
  }

  cancelEditApp(app: ManageAppRow): void {
    app.editing = false;
  }

  saveApp(app: ManageAppRow): void {
    if (!this.organizationId) return;
    this.busy = true;
    this.organizationService
      .updateApplication(this.organizationId, app.applicationId, {
        name: app.draftName.trim(),
        adminEmail: app.draftAdminEmail.trim(),
        contentAccessScope: app.draftContentAccessScope || "",
      })
      .subscribe({
        next: (res) => {
          this.busy = false;
          app.name = res?.name || app.draftName;
          app.adminEmail = res?.adminEmail || app.draftAdminEmail;
          app.contentAccessScope = res?.contentAccessScope || "";
          app.editing = false;
          this.toastr.success("Application updated");
        },
        error: (err) => {
          this.busy = false;
          this.toastr.error(err?.error?.error || "Update failed");
        },
      });
  }

  saveOrgContentAccess(): void {
    if (!this.organizationId || !this.isOrgAdmin) return;
    this.busy = true;
    this.organizationService
      .updateOrganization(this.organizationId, {
        contentAccessScope: this.orgContentAccessScope || "TeamOnly",
      })
      .subscribe({
        next: (res) => {
          this.busy = false;
          this.orgContentAccessScope =
            res?.contentAccessScope || this.orgContentAccessScope || "TeamOnly";
          this.orgContext.load(this.organizationId).subscribe();
          this.toastr.success("Library access updated");
        },
        error: (err) => {
          this.busy = false;
          this.toastr.error(err?.error?.error || "Update failed");
        },
      });
  }

  saveOrgPublicContentPublish(): void {
    if (!this.organizationId || !this.isOrgAdmin) return;
    this.busy = true;
    this.organizationService
      .updateOrganization(this.organizationId, {
        allowPublicContentPublish: !!this.orgAllowPublicContentPublish,
      })
      .subscribe({
        next: (res) => {
          this.busy = false;
          this.orgAllowPublicContentPublish =
            this.resolveAllowPublicContentPublish(res);
          this.orgContext.load(this.organizationId).subscribe();
          this.toastr.success("Public content publish setting updated");
        },
        error: (err) => {
          this.busy = false;
          this.toastr.error(err?.error?.error || "Update failed");
        },
      });
  }

  private resolveAllowPublicContentPublish(org: any): boolean {
    if (!org) return true;
    if (
      org.allowPublicContentPublish === false ||
      org.AllowPublicContentPublish === false
    ) {
      return false;
    }
    if (
      org.allowPublicContentPublish === true ||
      org.AllowPublicContentPublish === true
    ) {
      return true;
    }
    const access = String(
      org.memberAccountAccess || org.MemberAccountAccess || "InternalAndPublic"
    );
    return access.toLowerCase() !== "internalonly";
  }

  contentAccessOptionsForApp(): { value: string; label: string }[] {
    const base = this.isOrgAdmin
      ? this.contentAccessOptions
      : this.contentAccessOptions.filter(
          (o) => o.value === "TeamOnly" || o.value === "ApplicationWide"
        );
    return [this.inheritAccessOption, ...base];
  }

  contentAccessOptionsForTeam(): { value: string; label: string }[] {
    let base = this.contentAccessOptions.filter((o) => o.value === "TeamOnly");
    if (this.isOrgAdmin) base = this.contentAccessOptions;
    else if (this.isAppAdmin) {
      base = this.contentAccessOptions.filter(
        (o) => o.value === "TeamOnly" || o.value === "ApplicationWide"
      );
    }
    return [this.inheritAccessOption, ...base];
  }

  deleteApp(app: ManageAppRow): void {
    if (!this.isOrgAdmin || !this.organizationId) return;
    if (!confirm(`Delete application "${app.name}" and its teams?`)) return;
    this.busy = true;
    this.organizationService
      .deleteApplication(this.organizationId, app.applicationId)
      .subscribe({
        next: () => {
          this.busy = false;
          this.toastr.success("Application deleted");
          this.refresh();
        },
        error: (err) => {
          this.busy = false;
          this.toastr.error(err?.error?.error || "Delete failed");
        },
      });
  }

  startAddTeam(app: ManageAppRow): void {
    app.addingTeam = true;
    app.newTeamName = "";
    app.newTeamAdminEmail = "";
  }

  cancelAddTeam(app: ManageAppRow): void {
    app.addingTeam = false;
  }

  createTeam(app: ManageAppRow): void {
    if (!this.organizationId || !app.newTeamName.trim() || !app.newTeamAdminEmail.trim()) {
      this.toastr.error("Team name and admin email are required");
      return;
    }
    this.busy = true;
    this.organizationService
      .createTeam(this.organizationId, app.applicationId, {
        name: app.newTeamName.trim(),
        adminEmail: app.newTeamAdminEmail.trim(),
      })
      .subscribe({
        next: () => {
          this.busy = false;
          app.addingTeam = false;
          this.toastr.success("Team created — invite sent (pending acceptance)");
          this.refresh();
        },
        error: (err) => {
          this.busy = false;
          this.toastr.error(err?.error?.error || "Create team failed");
        },
      });
  }

  startEditTeam(team: ManageTeamRow): void {
    team.editing = true;
    team.draftName = team.name;
    team.draftAdminEmail = team.adminEmail;
    team.draftContentAccessScope = team.contentAccessScope || "";
  }

  cancelEditTeam(team: ManageTeamRow): void {
    team.editing = false;
  }

  saveTeam(app: ManageAppRow, team: ManageTeamRow): void {
    if (!this.organizationId) return;
    this.busy = true;
    this.organizationService
      .updateTeam(this.organizationId, app.applicationId, team.teamId, {
        name: team.draftName.trim(),
        adminEmail: team.draftAdminEmail.trim(),
        contentAccessScope: team.draftContentAccessScope || "",
      })
      .subscribe({
        next: (res) => {
          this.busy = false;
          team.name = res?.name || team.draftName;
          team.adminEmail = res?.adminEmail || team.draftAdminEmail;
          team.contentAccessScope = res?.contentAccessScope || "";
          team.editing = false;
          this.toastr.success("Team updated");
        },
        error: (err) => {
          this.busy = false;
          this.toastr.error(err?.error?.error || "Update failed");
        },
      });
  }

  deleteTeam(app: ManageAppRow, team: ManageTeamRow): void {
    if (!this.canDeleteTeam(app) || !this.organizationId) return;
    if (!confirm(`Delete team "${team.name}"?`)) return;
    this.busy = true;
    this.organizationService
      .deleteTeam(this.organizationId, app.applicationId, team.teamId)
      .subscribe({
        next: () => {
          this.busy = false;
          this.toastr.success("Team deleted");
          this.refresh();
        },
        error: (err) => {
          this.busy = false;
          this.toastr.error(err?.error?.error || "Delete failed");
        },
      });
  }

  canEditApp(app: ManageAppRow): boolean {
    return this.orgContext.canManageApplications() && this.membershipCoversApp(app.applicationId);
  }

  canEditTeam(app: ManageAppRow, team: ManageTeamRow): boolean {
    return (
      this.orgContext.canManageTeams() &&
      this.membershipCoversTeam(app.applicationId, team.teamId)
    );
  }

  canDeleteTeam(app: ManageAppRow): boolean {
    return this.canEditApp(app);
  }

  private membershipCoversApp(applicationId?: string | null): boolean {
    if (this.isOrgAdmin) return true;
    if (!applicationId) return false;
    return this.memberships.some((m) => {
      const mApp = m.applicationId || m.ApplicationId;
      const mTeam = m.teamId || m.TeamId;
      if (!mApp && !mTeam) return true; // org-wide
      return mApp === applicationId;
    });
  }

  private membershipCoversTeam(
    applicationId?: string | null,
    teamId?: string | null
  ): boolean {
    if (this.isOrgAdmin) return true;
    if (!teamId && !applicationId) return false;
    return this.memberships.some((m) => {
      const mApp = m.applicationId || m.ApplicationId;
      const mTeam = m.teamId || m.TeamId;
      if (!mApp && !mTeam) return true;
      if (mTeam) return mTeam === teamId;
      return !!applicationId && mApp === applicationId;
    });
  }

  canAddTeam(app: ManageAppRow): boolean {
    return this.canEditApp(app);
  }

  acceptInvite(invite: any): void {
    const orgId = invite.organizationId || invite.OrganizationId;
    const inviteId = invite.inviteId || invite.InviteId;
    const appId = invite.applicationId || invite.ApplicationId || null;
    const teamId = invite.teamId || invite.TeamId || null;
    if (!orgId || !inviteId) return;

    let details: AcceptInviteRequest | null = null;
    try {
      const raw = sessionStorage.getItem(orgInviteEmployeeStorageKey(inviteId));
      if (raw) {
        details = JSON.parse(raw) as AcceptInviteRequest;
      }
    } catch {
      details = null;
    }

    this.organizationService.acceptInvite(orgId, inviteId, details).subscribe({
      next: (membership) => {
        try {
          sessionStorage.removeItem(orgInviteEmployeeStorageKey(inviteId));
        } catch {
          /* ignore */
        }
        const preferredApp =
          membership?.applicationId ||
          membership?.ApplicationId ||
          appId ||
          null;
        const preferredTeam =
          membership?.teamId || membership?.TeamId || teamId || null;

        this.orgContext
          .load(orgId, preferredApp, preferredTeam)
          .subscribe(() => {
            this.toastr.success(
              "Invite accepted — your organization settings are updated"
            );
            this.syncOrgFromContext();
            this.refresh();
          });
      },
      error: (err) =>
        this.toastr.error(err?.error?.error || "Accept failed"),
    });
  }

  private handleInviteDeepLink(): void {
    const inviteId = this.route.snapshot.queryParamMap.get("inviteId");
    if (!inviteId) return;
    // Wait for myInvites to load, then accept matching invite
    const tryAccept = () => {
      const match = (this.myInvites || []).find(
        (i) => i.inviteId === inviteId || i.InviteId === inviteId
      );
      if (match) {
        this.acceptInvite(match);
        return;
      }
      this.toastr.info(
        "Open My pending invites below to accept your organization invite."
      );
    };
    if (this.myInvites?.length) {
      tryAccept();
    } else {
      setTimeout(tryAccept, 800);
    }
  }

  loadOwned(): void {
    if (!this.organizationId || !this.discontinueEmail.trim()) return;
    this.organizationService
      .getOwnedContent(this.organizationId, this.discontinueEmail.trim())
      .subscribe({
        next: (res) => (this.ownedItems = res?.items || []),
        error: (err) =>
          this.toastr.error(err?.error?.error || "Could not load owned content"),
      });
  }

  discontinue(): void {
    if (!this.organizationId || !this.discontinueEmail.trim()) return;
    if (this.ownedItems.length && !this.reassignmentEmail.trim()) {
      this.toastr.error("Enter a reassignment email for owned content first");
      return;
    }
    this.busy = true;
    this.organizationService
      .discontinueMember({
        organizationId: this.organizationId,
        email: this.discontinueEmail.trim(),
        reassignments: this.ownedItems.map((item) => ({
          contentType: item.contentType,
          rowKey: item.rowKey,
          newOwnerEmail: this.reassignmentEmail.trim(),
        })),
      })
      .subscribe({
        next: (res) => {
          this.busy = false;
          if (res?.success) {
            this.toastr.success(res.message || "Member discontinued");
            this.ownedItems = [];
            this.discontinueEmail = "";
            this.reassignmentEmail = "";
          } else {
            this.ownedItems = res?.remainingOwnedContent || this.ownedItems;
            this.toastr.error(res?.message || "Reassignment required");
          }
        },
        error: (err) => {
          this.busy = false;
          this.toastr.error(err?.error?.error || "Discontinue failed");
        },
      });
  }

  openLibraryItem(item: any): void {
    if (!item?.contentId) return;
    if (item.contentType === "GuidePack") {
      this.router.navigate(["/view-guide-pack", item.contentId]);
      return;
    }
    this.flowNavigation.navigateToPublishedFlow(
      item.title || "flow",
      item.contentId
    );
  }

  libraryCoverUrl(item: any): string | null {
    if (!item) return null;
    const cover = String(item.coverUrl || item.CoverUrl || "").trim();
    return cover || null;
  }

  private loadManagedApps(apps: any[]): void {
    if (!this.organizationId || !apps.length) {
      this.managedApps = [];
      return;
    }
    forkJoin(
      apps.map((app) =>
        this.organizationService
          .listTeams(this.organizationId, app.applicationId)
          .pipe(catchError(() => of([])))
      )
    ).subscribe((teamsByApp) => {
      this.managedApps = apps.map((app, i) => ({
        applicationId: app.applicationId,
        name: app.name || "",
        adminEmail: app.adminEmail || "",
        editing: false,
        draftName: app.name || "",
        draftAdminEmail: app.adminEmail || "",
        contentAccessScope: app.contentAccessScope || "",
        draftContentAccessScope: app.contentAccessScope || "",
        addingTeam: false,
        newTeamName: "",
        newTeamAdminEmail: "",
        teams: ((teamsByApp[i] as any[]) || []).map((t) => ({
          teamId: t.teamId,
          name: t.name || "",
          adminEmail: t.adminEmail || "",
          editing: false,
          draftName: t.name || "",
          draftAdminEmail: t.adminEmail || "",
          contentAccessScope: t.contentAccessScope || "",
          draftContentAccessScope: t.contentAccessScope || "",
        })),
      }));
    });
  }

  private syncOrgFromContext(): void {
    const snap = this.orgContext.snapshot;
    this.memberships = (snap.memberships || []).filter(
      (m) =>
        !snap.activeOrganization?.organizationId ||
        m.organizationId === snap.activeOrganization.organizationId
    );
    // Feature matrix for the assigned role (not hardcoded role-name checks).
    this.isOrgAdmin = this.orgContext.isOrgAdminUser();
    this.isAppAdmin = this.orgContext.canManageApplications();
    this.isTeamAdmin = this.orgContext.canManageTeams();
    this.canManageMemberRoles = this.orgContext.canManageMemberRoles();
    this.canManageOrganization = this.orgContext.canManageOrganization();
    this.canApproveContent = this.orgContext.canApproveContent(
      snap.activeOrganization?.organizationId,
      snap.activeApplicationId,
      snap.activeTeamId
    );

    const seen = new Map<string, string>();
    for (const m of snap.memberships || []) {
      const id = m.organizationId;
      if (!id || seen.has(id)) continue;
      seen.set(id, m.organizationName || id);
    }
    if (snap.activeOrganization?.organizationId) {
      seen.set(
        snap.activeOrganization.organizationId,
        snap.activeOrganization.name ||
          seen.get(snap.activeOrganization.organizationId) ||
          snap.activeOrganization.organizationId
      );
    }
    this.orgOptions = Array.from(seen.entries()).map(([organizationId, name]) => ({
      organizationId,
      name,
    }));
    this.organizationId =
      snap.activeOrganization?.organizationId ||
      this.orgOptions[0]?.organizationId ||
      "";
    this.organizationName =
      snap.activeOrganization?.name ||
      this.orgOptions.find((o) => o.organizationId === this.organizationId)?.name ||
      "";
    if (snap.terminology) {
      this.applyTerminology(snap.terminology);
    } else {
      this.accessRoles = createDefaultAccessRoles();
      this.rebuildDerivedLists();
    }
    this.rebuildInviteScopeOptions();
  }

  private rebuildInviteScopeOptions(): void {
    const blank = (v: any) => {
      const s = String(v ?? "").trim().toLowerCase();
      return !s || s === "none" || s === "null" || /^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(s);
    };
    const canInvite = (m: any) => {
      const role = String(m?.role || m?.Role || "");
      return roleAllowsFeature(this.accessRoles, role, "inviteMembers");
    };
    const inviters = this.memberships.filter(canInvite);
    const orgWide = inviters.filter((m) => blank(m.applicationId) && blank(m.teamId));
    const orgWideManagers = this.memberships.filter(
      (m) =>
        blank(m.applicationId) &&
        blank(m.teamId) &&
        roleAllowsFeature(this.accessRoles, String(m?.role || m?.Role || ""), "manageOrganization")
    );

    const options: InviteScope[] = [];
    if (orgWide.length || orgWideManagers.length) options.push("Organization");
    if (inviters.some((m) => blank(m.teamId))) options.push("Application");
    if (inviters.length) options.push("Team");
    this.inviteScopeOptions = options.length ? options : ["Team"];

    if (!this.inviteScopeOptions.includes(this.inviteScope)) {
      this.inviteScope = this.inviteScopeOptions[0];
    }

    // Team-scoped inviters: preselect their own application/team.
    if (!this.inviteScopeOptions.includes("Application")) {
      const own = inviters.find((m) => !blank(m.teamId));
      if (own && !this.applicationId) {
        this.applicationId = own.applicationId;
        this.onApplicationChange();
        this.teamId = own.teamId;
      }
    }
  }
}
